import { customElement, property } from "lit/decorators.js";
import { LitElement, html, css } from "lit";
import { PresetController } from "../helper/controllers/preset-controller";


/**
 * @element sky-row
 *
 * @summary Lightweight semantic row wrapper for use inside `sky-grid`.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/grid
 * @dependency sky-grid
 *
 * @slot - Default slot for child content.
 *
 * @Behavior
 * - Renders as `display: contents` so it does not add layout wrappers.
 * - Preserves semantic grouping for rows while children participate directly in parent layout.
 *
 * @example
 * ```html
 * <sky-grid columns="3" gap="12px">
 *   <sky-row>
 *     <div>Account</div>
 *     <div>Status</div>
 *     <div>Actions</div>
 *   </sky-row>
 * </sky-grid>
 * ```
 * ```vue
 * <template>
 *   <sky-grid columns="3" gap="12px">
 *     <sky-row>
 *       <div>Account</div>
 *       <div>Status</div>
 *       <div>Actions</div>
 *     </sky-row>
 *   </sky-grid>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-grid columns="3" gap="12px">
 *       <sky-row>
 *         <div>Account</div>
 *         <div>Status</div>
 *         <div>Actions</div>
 *       </sky-row>
 *     </sky-grid>
 *   );
 * }
 * ```
 */
@customElement("sky-row")
export class SkyRow extends LitElement {

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: contents;
    }
  `;

  override render() {
    return html`<slot></slot>`;
  }
}